import {useMemo, useEffect, useRef, useState} from 'react'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCustomSdk as useCustomSdkStandard} from '_customHooks/use-custom-sdk'
import {useCustomSdk as useCustomSdkDY} from '_customHooks/use-custom-sdk-DY'
import type {PageIdType, CategoryType, ProductSearchType, PageDesignerType} from '_types'
import {getDyConsentAccepted} from './helpers'

type CustomSdkProviderParams = {
    type?: string
    urlSegment: string
    params: Record<string, unknown>
    inheritedPageId?: PageIdType
    inheritedCategory?: CategoryType
    userLoadMore: {current: boolean}
}

type CustomSdkProviderResult = {
    data: {
        pageIdData?: PageIdType
        categoryData?: CategoryType
        productData?: ProductSearchType
        pageDesignerData?: PageDesignerType
    } | null
    isLoading: boolean
    refetch: () => Promise<unknown>
    isRefetching: boolean
    error: unknown
    isDynamicYieldActive: boolean
    isDynamicYieldEligible: boolean
    isDynamicYieldResolved: boolean
    categoryActivation?: string
}

const useCustomSdkProvider = ({
    type,
    urlSegment,
    params,
    inheritedPageId,
    inheritedCategory,
    userLoadMore,
}: CustomSdkProviderParams): CustomSdkProviderResult => {
    const {customParams} = getConfig()
    const dynamicYieldConfig = customParams?.dynamicYieldConfig
    const {site} = useMultiSite()
    const {req} = useServerContext()
    const dyConfigForSite = site?.id ? dynamicYieldConfig?.[site.id] : null
    const hasDynamicYieldConfig = Boolean(dyConfigForSite?.instanceId)

    const cookieStr = typeof document === 'undefined' ? req?.headers?.cookie : document.cookie
    const dyConsentByRouteRef = useRef<Record<string, boolean>>({})
    if (dyConsentByRouteRef.current[urlSegment] === undefined) {
        dyConsentByRouteRef.current[urlSegment] = getDyConsentAccepted()
    }
    const dyConsentAccepted = dyConsentByRouteRef.current[urlSegment]
    const akamaiDeviceHeader = Array.isArray(req?.headers?.['x-shop-request']) ? req?.headers?.['x-shop-request'][0] : req?.headers?.['x-shop-request']
    const abDyBucketFromHeader = akamaiDeviceHeader === 'B'
    const abDyBucketFromCookie = (() => {
        if (!cookieStr) return undefined
        const cookieMap = cookieStr.split(';').reduce(
            (acc, entry) => {
                const [key, ...valArr] = entry.trim().split('=')
                acc[key] = valArr.join('=')
                return acc
            },
            {} as Record<string, string>,
        )
        if (cookieMap.sgm_cookie === 'B') return true
        if (cookieMap.sgm_cookie === 'A') return false
        return undefined
    })()
    const abDyBucket = typeof abDyBucketFromCookie === 'boolean' ? abDyBucketFromCookie : abDyBucketFromHeader

    const [standardSearchMode, setStandardSearchMode] = useState<'category-only' | 'full'>('category-only')
    // la searchMode permette di decidere se la search va fatta solo con categoryId (category-only)
    // o con tutti i parametri (full), in modo da escludere i parametri di ricerca quando non serve,
    // ad esempio quando si è in una categoria con DY attivo e si vuole sfruttare DY per fare la ricerca,
    // evitando di passare a DY dei parametri di ricerca che potrebbero non essere gestiti correttamente
    // e causare problemi di performance. La searchMode viene inizialmente impostata a 'category-only' per
    // sfruttare DY quando possibile, ma se viene rilevato che la categoria non ha DY attivo, allora viene
    // impostata a 'full' per assicurare che la ricerca funzioni correttamente anche senza DY.
    const {
        data: sdkDataStandard,
        isLoading: isLoadingStandard,
        refetch: refetchStandard,
        isRefetching: isRefetchingStandard,
        error: errorStandard,
    } = useCustomSdkStandard({
        type,
        urlSegment,
        params,
        inheritedPageId,
        inheritedCategory,
        userLoadMore,
        searchMode: standardSearchMode,
        dyContext: {
            hasDynamicYieldConfig,
            abDyBucket,
            dyConsentAccepted,
        },
    })
    const resolvedCategory = sdkDataStandard?.categoryData ?? inheritedCategory
    const categoryActivation = sdkDataStandard?.categoryData?.c_dynamicYieldActivation as string | undefined
    const isSearchParam = Boolean(params?.isSearch)
    const isSimpleSearch = Boolean(params?.isSearch && params?.q)
    // La logica di attivazione di DY è la seguente:
    // - se la categoria ha c_dynamicYieldActivation diverso da null/undefined/empty, allora si può attivare DY
    // - se c_dynamicYieldActivation è uguale a 'DY', allora DY è attivo per tutti gli utenti
    // - se c_dynamicYieldActivation è uguale a 'AB', allora DY è attivo solo per gli utenti che sono nell'AB bucket di DY e che hanno accettato il consenso (dyConsentAccepted)
    const standardPageId = sdkDataStandard?.pageIdData
    const standardCategoryId = sdkDataStandard?.categoryData?.id
    const standardPageMatchesUrl = !standardPageId?.urlSegment || standardPageId.urlSegment === urlSegment
    const standardCategoryMatchesPage = !standardPageId?.resourceId || standardPageId.resourceId === standardCategoryId
    const isDynamicYieldResolved = isSearchParam || (!isLoadingStandard && standardPageMatchesUrl && standardCategoryMatchesPage)
    const isDynamicYieldEligible = hasDynamicYieldConfig
        ? isSimpleSearch
            ? Boolean(abDyBucket && dyConsentAccepted)
            : categoryActivation === 'DY' || (categoryActivation === 'AB' && abDyBucket && dyConsentAccepted)
        : false

    const shouldUseDyCandidate = isDynamicYieldEligible && standardPageMatchesUrl && standardCategoryMatchesPage
    const shouldUseDyLockedRef = useRef<boolean | null>(null)
    const [shouldUseDy, setShouldUseDy] = useState<boolean>(false)
    const previousUrlSegmentRef = useRef<string>(urlSegment)

    useEffect(() => {
        if (previousUrlSegmentRef.current !== urlSegment) {
            shouldUseDyLockedRef.current = null
            previousUrlSegmentRef.current = urlSegment
        }

        if (isSimpleSearch) {
            // For simple search: use DY only if in bucket B with consent, otherwise use standard
            const shouldUseDyForSimpleSearch = abDyBucket && dyConsentAccepted
            setShouldUseDy(shouldUseDyForSimpleSearch)
            shouldUseDyLockedRef.current = shouldUseDyForSimpleSearch
            if (!shouldUseDyForSimpleSearch && standardSearchMode !== 'full') {
                setStandardSearchMode('full')
            }
            return
        }

        if (shouldUseDyLockedRef.current !== null) return

        const standardResolved = !isLoadingStandard && standardPageMatchesUrl && standardCategoryMatchesPage && Boolean(sdkDataStandard?.categoryData)
        if (standardResolved) {
            shouldUseDyLockedRef.current = shouldUseDyCandidate
            setShouldUseDy(shouldUseDyCandidate)
            const nextMode = shouldUseDyCandidate ? 'category-only' : 'full'
            if (standardSearchMode !== nextMode) {
                setStandardSearchMode(nextMode)
            }
        }
    }, [
        isSimpleSearch,
        isLoadingStandard,
        standardPageMatchesUrl,
        standardCategoryMatchesPage,
        sdkDataStandard?.categoryData,
        shouldUseDyCandidate,
        standardSearchMode,
        abDyBucket,
        dyConsentAccepted,
    ])

    const dyInheritedPageId = standardPageMatchesUrl ? standardPageId : undefined

    const {
        data: sdkDataDy,
        isLoading: isLoadingDy,
        refetch: refetchDy,
        isRefetching: isRefetchingDy,
        error: errorDy,
    } = useCustomSdkDY({
        type,
        urlSegment,
        params,
        inheritedPageId: dyInheritedPageId,
        inheritedCategory: resolvedCategory,
        userLoadMore,
        enabled: shouldUseDy && standardPageMatchesUrl,
    })

    const data = useMemo(() => {
        if (!sdkDataStandard && !sdkDataDy) return null
        return {
            pageIdData: (shouldUseDy ? sdkDataDy?.pageIdData : sdkDataStandard?.pageIdData) ?? sdkDataStandard?.pageIdData,
            categoryData: sdkDataStandard?.categoryData ?? sdkDataDy?.categoryData ?? undefined,
            productData: (shouldUseDy ? sdkDataDy?.productData : sdkDataStandard?.productData) ?? undefined,
            pageDesignerData: (shouldUseDy ? sdkDataDy?.pageDesignerData : sdkDataStandard?.pageDesignerData) ?? sdkDataStandard?.pageDesignerData ?? undefined,
        }
    }, [sdkDataStandard, sdkDataDy, shouldUseDy])

    return {
        data,
        isLoading: shouldUseDy ? isLoadingDy : isLoadingStandard,
        refetch: shouldUseDy ? refetchDy : refetchStandard,
        isRefetching: shouldUseDy ? isRefetchingDy : isRefetchingStandard,
        error: shouldUseDy ? errorDy : errorStandard,
        isDynamicYieldActive: shouldUseDy,
        isDynamicYieldEligible,
        isDynamicYieldResolved,
        categoryActivation,
    }
}

export {useCustomSdkProvider}
